/**
 * Activity Store (Actividad tab)
 */

import { useCallback } from 'react';
import { createStore } from '@commandDrivenStateEngine/core/createStore';
import { Policy } from '@commandDrivenStateEngine/core/policies';
import type { CommandMetaInput, Dispatch } from '@commandDrivenStateEngine/core/types';
import { ClientLibraryType } from '@bett3r-dev/teselly-client-library';
import useAuth from '@contexts/useAuth/useAuth';
import { initialState, ActivityState } from './state';
import { handlers } from './handlers';
import { invariants } from './invariants';
import { ActivityCommand } from './commands';
import {
  createExecutionsSubscriptionPolicy,
  createTracePolicy
} from './policies';

export { selectors } from './invariants';

const store = createStore({
  name: 'automation-activity',
  initialState,
  handlers,
  invariants
});

export const {
  useSelector: useActivitySelector,
  useErrors: useActivityErrors,
  usePolicies: useActivityPolicies,
  getState: getActivityState,
  dispatch: dispatchActivityCommand
} = store;

/**
 * Dispatch hook that attaches the authenticated user to user-sourced commands
 * so the `canApply` capability gates can authorize them. The engine core stays
 * auth-agnostic; this web-app boundary supplies the actor. An explicit
 * `meta.user` (e.g. from tests) is left untouched.
 */
export const useActivityDispatch = (): Dispatch<ActivityCommand> => {
  const auth = useAuth();
  const user = auth?.user ?? null;
  return useCallback<Dispatch<ActivityCommand>>(
    ( command, metaInput: CommandMetaInput ) =>
      store.dispatch(
        command,
        metaInput.source === 'user' && metaInput.user === undefined
          ? { ...metaInput, user }
          : metaInput
      ),
    [user]
  );
};

/**
 * Policy factory - creates policies for the activity store.
 *
 * - executionsSubscriptionPolicy: the flat per-execution list driver — a
 *   one-shot `RuleExecutionsSearch` snapshot + status-counts query, kept live by
 *   a signal-mode `RuleExecutions` subscription with a debounced refetch. Also
 *   feeds the LiveStrip / page-header running-count via `statusCounts`.
 * - tracePolicy: per-execution trace load + retry submission.
 */
export const createActivityPolicies = (
  backend: ClientLibraryType
): Policy<ActivityState, ActivityCommand>[] => [
  createExecutionsSubscriptionPolicy( backend ),
  createTracePolicy( backend )
];
